import type { ReactNode } from 'react';
import { VoiceRecorderButton } from '../atoms/VoiceRecorderButton';
import { useTranslation } from '../../i18n/useTranslation';
import { useOptionalCapture } from './CaptureContext';

export interface RecordNoteButtonProps {
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  /** Tank of the note; default: the tank of the current page / the active one. */
  aquariumId?: string;
  className?: string;
  /** Label; default "Record note". */
  children?: ReactNode;
}

/**
 * "Record note" for page headers and empty states: starts the app-wide
 * recorder (CaptureContext). Renders nothing where voice notes are unavailable
 * (feature off, browser cannot record, signed out), or while a note is recorded.
 */
export function RecordNoteButton({ variant = 'secondary', size = 'md', fullWidth = false, aquariumId, className, children }: RecordNoteButtonProps) {
  const { t } = useTranslation();
  const capture = useOptionalCapture();
  if (!capture?.available) return null;
  const busy = capture.phase === 'recording' || capture.phase === 'paused' || capture.phase === 'requesting';
  return (
    <VoiceRecorderButton
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      className={className}
      disabled={busy}
      onStart={() => capture.startRecording(aquariumId ? { aquariumId } : {})}
    >
      {children ?? t('voice.recorder.start')}
    </VoiceRecorderButton>
  );
}
